import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';

// ─── CONFIG ────────────────────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyBuD4UwwE1ya8bGO-9t5r_9oOhsPUJ_SjE",
  authDomain: "just-salento.firebaseapp.com",
  projectId: "just-salento",
  storageBucket: "just-salento.appspot.com",
  messagingSenderId: "386735750952",
  appId: "1:386735750952:web:6dcff4bebf82e2bce21a1d"
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// ─── AUTH ───────────────────────────────────────────────────────────────────

export async function registerBeachAdmin({ email, password, nomeLido, indirizzo, telefono }) {
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  const uid = credential.user.uid;

  await setDoc(doc(db, 'admins', uid), {
    email,
    beachCode: uid,
    createdAt: serverTimestamp(),
    approved: true,
    onboardingCompleto: false,
  });

  await setDoc(doc(db, 'lidi', uid), {
    codice: uid,
    nome: nomeLido,
    indirizzo,
    telefono,
    email,
    servizi: [],
    gridCols: 10,
    gridRows: 5,
    galleria_fotografica: [],
    createdAt: serverTimestamp(),
  });

  return uid;
}

export async function loginAdmin(email, password) {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential.user;
}

export async function logoutAdmin() {
  await signOut(auth);
}

export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

// ─── ADMIN / LIDO ───────────────────────────────────────────────────────────

export async function getAdminData(uid) {
  const snap = await getDoc(doc(db, 'admins', uid));
  return snap.exists() ? { uid, ...snap.data() } : null;
}

export async function getBeach(beachCode) {
  const snap = await getDoc(doc(db, 'lidi', beachCode));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateBeach(beachCode, data) {
  await setDoc(doc(db, 'lidi', beachCode), data, { merge: true });
}

export async function completeOnboarding(uid) {
  await updateDoc(doc(db, 'admins', uid), { onboardingCompleto: true });
}

// ─── SERVIZI ────────────────────────────────────────────────────────────────

export async function saveServizi(beachCode, servizi) {
  await setDoc(doc(db, 'lidi', beachCode), { servizi }, { merge: true });
}

// ─── AREE ───────────────────────────────────────────────────────────────────

export async function saveArea(beachCode, area) {
  if (area.id) {
    const { id, ...data } = area;
    await setDoc(doc(db, `lidi/${beachCode}/aree`, id), data, { merge: true });
    return id;
  } else {
    const ref = await addDoc(collection(db, `lidi/${beachCode}/aree`), area);
    return ref.id;
  }
}

export async function getAree(beachCode) {
  const snap = await getDocs(collection(db, `lidi/${beachCode}/aree`));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ─── LAYOUT SPIAGGIA ────────────────────────────────────────────────────────

export async function saveLayoutTemplate(beachCode, { gridCols, gridRows, celle }) {
  await setDoc(doc(db, 'lidi', beachCode), { gridCols, gridRows, layoutTemplate: celle }, { merge: true });
}

export async function analizzaConflitti(beachCode, { celle, dal, al }) {
  const [startY, startM, startD] = dal.split('-').map(Number);
  const [endY, endM, endD] = al.split('-').map(Number);

  const nuovoTemplate = {};
  celle.filter(c => c.type !== 'vuoto').forEach(c => {
    const id = `${String.fromCharCode(65 + c.row)}${c.col + 1}`;
    nuovoTemplate[id] = c.type;
  });

  const conflitti = [];
  let y = startY, m = startM, d = startD;

  while (y < endY || (y === endY && m < endM) || (y === endY && m === endM && d <= endD)) {
    const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const snap = await getDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr));

    if (snap.exists()) {
      const layout = snap.data().layout || [];
      const celleOccupate = layout.filter(c =>
        c.status === 'occupied' || c.status === 'reserved' || c.status === 'blocked'
      );
      for (const cella of celleOccupate) {
        const nuovoTipo = nuovoTemplate[cella.id];
        if (!nuovoTipo) {
          conflitti.push({ dateStr, cellaId: cella.id, tipoVecchio: cella.type, tipoNuovo: null });
        } else if (nuovoTipo !== cella.type) {
          conflitti.push({ dateStr, cellaId: cella.id, tipoVecchio: cella.type, tipoNuovo: nuovoTipo });
        }
      }
    }

    d++;
    const daysInMonth = new Date(y, m, 0).getDate();
    if (d > daysInMonth) { d = 1; m++; }
    if (m > 12) { m = 1; y++; }
  }

  return conflitti;
}

export async function pubblicaLayout(beachCode, { celle, dal, al }) {
  const [startY, startM, startD] = dal.split('-').map(Number);
  const [endY, endM, endD] = al.split('-').map(Number);

  const nuovoTemplate = {};
  celle.filter(c => c.type !== 'vuoto').forEach(c => {
    nuovoTemplate[`${c.col}_${c.row}`] = c;
  });

  let y = startY, m = startM, d = startD;

  while (y < endY || (y === endY && m < endM) || (y === endY && m === endM && d <= endD)) {
    const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const snap = await getDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr));

    let layout;
    let areaPrenotazioni = {};

    if (!snap.exists()) {
      // ✅ FIX: id sempre assegnato anche per layout nuovi
      layout = celle.filter(c => c.type !== 'vuoto').map(c => ({
        ...c,
        id: `${String.fromCharCode(65 + c.row)}${c.col + 1}`,
        status: 'free',
      }));
    } else {
      const existing = snap.data();
      areaPrenotazioni = existing.areaPrenotazioni ?? {};
      const existingMap = {};
      (existing.layout || []).forEach(c => { existingMap[c.id] = c; });
      layout = celle.filter(c => c.type !== 'vuoto').map(c => {
        const id = `${String.fromCharCode(65 + c.row)}${c.col + 1}`;
        const ex = existingMap[id];
        if (ex && (ex.status === 'occupied' || ex.status === 'reserved' || ex.status === 'blocked')) {
          return { ...c, id, status: ex.status };
        }
        return { ...c, id, status: 'free' };
      });
    }

    await setDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr), {
      dateStr, layout, areaPrenotazioni, updatedAt: serverTimestamp(),
    });

    d++;
    const daysInMonth = new Date(y, m, 0).getDate();
    if (d > daysInMonth) { d = 1; m++; }
    if (m > 12) { m = 1; y++; }
  }
}

export async function getLayoutByDate(beachCode, dateStr) {
  const snap = await getDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getAllLayouts(beachCode) {
  const snap = await getDocs(collection(db, `lidi/${beachCode}/disponibilita`));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ─── TARIFFE ────────────────────────────────────────────────────────────────

export async function getTariffe(beachCode) {
  const snap = await getDocs(
    query(collection(db, `lidi/${beachCode}/tariffe`), orderBy('dal', 'asc'))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function saveTariffa(beachCode, tariffa) {
  if (tariffa.id) {
    const { id, ...data } = tariffa;
    await updateDoc(doc(db, `lidi/${beachCode}/tariffe`, id), data);
    return id;
  } else {
    const ref = await addDoc(collection(db, `lidi/${beachCode}/tariffe`), tariffa);
    return ref.id;
  }
}

export async function deleteTariffa(beachCode, tariffaId) {
  await deleteDoc(doc(db, `lidi/${beachCode}/tariffe`, tariffaId));
}

export function calcolaPrezzo(tariffe, dateStr, servizioId, rowIndex, haFila) {
  const attive = tariffe.filter((t) => t.dal <= dateStr && t.al >= dateStr);
  if (!attive.length) return null;

  const daysBetween = (dal, al) =>
    Math.abs(new Date(al).getTime() - new Date(dal).getTime()) / 86400000;

  const sorted = [...attive].sort((a, b) => {
    const diffDurata = daysBetween(a.dal, a.al) - daysBetween(b.dal, b.al);
    if (diffDurata !== 0) return diffDurata;
    return b.dal.localeCompare(a.dal);
  });

  const tariffa = sorted[0];
  const key = haFila ? `${servizioId}_r${rowIndex}` : servizioId;
  return tariffa.prezzi?.[key] ?? null;
}

// ─── PRENOTAZIONI ───────────────────────────────────────────────────────────

export async function getOrdersByBeach(beachCode) {
  const q = query(
    collection(db, 'orders'),
    where('beachCode', '==', beachCode),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getOrdersByBeachAndDate(beachCode, date) {
  const q = query(
    collection(db, 'orders'),
    where('beachCode', '==', beachCode),
    where('selectedDate', 'array-contains', date)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function updateOrderStatus(orderId, status) {
  await updateDoc(doc(db, 'orders', orderId), { status });
}

export async function cancelOrder(orderId) {
  await updateOrderStatus(orderId, 'cancelled');
}

export async function createOrderManual({
  beachCode, servizioId, servizioLabel, celle,
  totalPrice, selectedDate, guestName, notes,
}) {
  const ref = await addDoc(collection(db, 'orders'), {
    beachCode,
    userId: 'admin_manual',
    guestName: guestName || '',
    servizioId,
    servizioLabel,
    celle,
    totalPrice,
    selectedDate,
    notes: notes || '',
    status: 'confirmed',
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

// ─── UTENTI ─────────────────────────────────────────────────────────────────

export async function getUserData(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { uid, ...snap.data() } : null;
}

// ─── PRENOTAZIONE MANUALE / BLOCCO ──────────────────────────────────────────

export function expandDates({ dateType, selectedDate, dateRange, dates, seasonalDays }) {
  if (dateType === 'single') return [selectedDate];
  if (dateType === 'multiple') return dates || [];

  if (dateType === 'range' || dateType === 'seasonal') {
    const result = [];
    const [startY, startM, startD] = dateRange.dal.split('-').map(Number);
    const [endY, endM, endD] = dateRange.al.split('-').map(Number);
    let y = startY, m = startM, d = startD;

    while (y < endY || (y === endY && m < endM) || (y === endY && m === endM && d <= endD)) {
      const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      if (dateType === 'range') {
        result.push(dateStr);
      } else {
        const dow = new Date(y, m - 1, d).getDay();
        if (seasonalDays.includes(dow)) result.push(dateStr);
      }
      d++;
      const daysInMonth = new Date(y, m, 0).getDate();
      if (d > daysInMonth) { d = 1; m++; }
      if (m > 12) { m = 1; y++; }
    }
    return result;
  }
  return [];
}

// ✅ FIX: signature aggiornata con isArea, servizioId, servizioLabel
// `source` è opzionale (default 'admin_manual') per differenziare gli ordini
// importati da spiagge.it, integrazioni future, ecc.
export async function createBooking(beachCode, {
  celle, guestName, guestPhone, guestEmail, notes,
  dateType, selectedDate, dateRange, dates, seasonalDays,
  totalPrice, status = 'confirmed',
  isArea = false, servizioId = null, servizioLabel = null,
  source = 'admin_manual',
}) {
  const allDates = expandDates({ dateType, selectedDate, dateRange, dates, seasonalDays });

  const orderData = {
    beachCode,
    celle,
    guestName: guestName || '',
    guestPhone: guestPhone || '',
    notes: notes || '',
    dateType,
    selectedDate: allDates,
    dateRange: dateRange || null,
    seasonalDays: seasonalDays || null,
    totalPrice: totalPrice || 0,
    status,
    source,
    isArea,                              // ✅ salvato in Firestore
    servizioId: servizioId || null,      // ✅ salvato in Firestore
    servizioLabel: servizioLabel || null, // ✅ salvato in Firestore
    createdAt: serverTimestamp(),
  };
  if (guestEmail) orderData.guestEmail = guestEmail;

  const ref = await addDoc(collection(db, 'orders'), orderData);

  // Aggiorna il contatore areaPrenotazioni se è una prenotazione di tipo area
  if (isArea && servizioId) {
    const qty = celle.find(c => c.type === servizioId || c.id === servizioId)?.quantita || 1;
    await Promise.all(allDates.map(async (dateStr) => {
      const snap = await getDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr));
      if (!snap.exists()) return;
      const current = snap.data().areaPrenotazioni ?? {};
      await updateDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr), {
        areaPrenotazioni: {
          ...current,
          [servizioId]: (current[servizioId] ?? 0) + qty,
        },
      });
    }));
  }

  // Filtra celle senza id (aree, decorazioni) per non marcare tutto il layout
  const celleIds = celle.map(c => c.id).filter(Boolean);
  if (!celleIds.length) return ref.id;

  const newStatus = status === 'confirmed' ? 'occupied' : 'reserved';

  await Promise.all(allDates.map(async (dateStr) => {
    const snap = await getDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr));
    if (!snap.exists()) return;
    const layout = snap.data().layout || [];
    const updated = layout.map(c =>
      celleIds.includes(c.id) ? { ...c, status: newStatus } : c
    );
    await updateDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr), { layout: updated });
  }));

  return ref.id;
}

export async function blockSpot(beachCode, params) {
  return createBooking(beachCode, { ...params, status: 'blocked' });
}

// ─── Import spiagge.it (CSV) ────────────────────────────────────────────────

// Estrae dagli ordini già in Firestore i Set degli identificativi spiagge.it
// già importati (letti dalle `notes`), così l'import può fare dedup.
// Ignora gli ordini `cancelled`: se un import è stato annullato, deve poter
// essere re-importato senza essere saltato dal dedup.
export async function loadSpiaggeItExistingIds(beachCode) {
  const q = query(
    collection(db, 'orders'),
    where('beachCode', '==', beachCode),
    where('source', '==', 'spiagge_it'),
  );
  const snap = await getDocs(q);
  const ids = new Set();
  snap.forEach(d => {
    const data = d.data();
    if (data.status === 'cancelled') return;
    const notes = data.notes || '';
    const m = /spiagge\.it #(\d+)/.exec(notes);
    if (m) ids.add(m[1]);
  });
  return ids;
}

// Assicura che il documento lidi/{beach}/disponibilita/{date} esista.
// Se manca, lo crea copiando il layout dal doc lido master.
// createBooking salta l'update del layout quando il doc non esiste, quindi
// senza questa ensure una prenotazione su una data non ancora materializzata
// lascerebbe la cella libera → rischio doppio-booking.
export async function ensureDisponibilita(beachCode, dateStr, layoutTemplate) {
  const ref = doc(db, `lidi/${beachCode}/disponibilita`, dateStr);
  const snap = await getDoc(ref);
  if (snap.exists()) return;
  await setDoc(ref, {
    dateStr,
    layout: layoutTemplate,
    areaPrenotazioni: {},
    updatedAt: serverTimestamp(),
  });
}

// Importa un piano di prenotazione spiagge.it: assicura la disponibilità per
// tutte le date coinvolte, poi crea l'ordine con source: 'spiagge_it'.
// Ritorna { orderId, verified: { updated: N, missing: N } } così l'UI può
// segnalare all'utente se il layout non è stato effettivamente aggiornato.
export async function importSpiaggeItPlan(beachCode, plan, layoutTemplate) {
  const { order, dates } = plan;
  await Promise.all(dates.map(d => ensureDisponibilita(beachCode, d, layoutTemplate)));
  const orderId = await createBooking(beachCode, { ...order, source: 'spiagge_it' });

  // Verifica post-scrittura: leggi ogni data e conta quante celle risultano
  // effettivamente aggiornate. Se qualcosa manca, l'UI mostrerà un warning.
  const expectedCellId = order.celle?.[0]?.id;
  const isArea = !!order.isArea;
  let updated = 0;
  let missing = 0;

  await Promise.all(dates.map(async (dateStr) => {
    const snap = await getDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr));
    if (!snap.exists()) { missing++; return; }
    const data = snap.data();

    if (isArea) {
      const srvId = order.servizioId;
      const count = data.areaPrenotazioni?.[srvId] || 0;
      if (count > 0) updated++;
      else missing++;
    } else {
      const layout = data.layout || [];
      const cell = layout.find(c => c.id === expectedCellId);
      if (cell && (cell.status === 'reserved' || cell.status === 'occupied' || cell.status === 'blocked')) {
        updated++;
      } else {
        missing++;
        // Log dettagliato per debug — utile per capire perché VENTURI & co non si aggiornano
        console.warn(`[Import] ${dateStr} · cella ${expectedCellId} NON aggiornata`, {
          orderId,
          celleInLayout: layout.length,
          layoutHasIds: layout.slice(0, 3).map(c => ({ id: c.id, col: c.col, row: c.row, status: c.status })),
        });
      }
    }
  }));

  return { orderId, verified: { updated, missing, total: dates.length } };
}

// areaQuantita: { [servizioId]: qty } — passa questo oggetto per decrementare
// le aree lettini. Ricavalo da order.areaQuantita (ordini app) oppure costruiscilo
// come { [servizioId]: celle[0].quantita } per gli ordini manuali del gestionale.
export async function cancelBookingAndRelease(beachCode, orderId, celle, allDates, areaQuantita = {}) {
  await updateDoc(doc(db, 'orders', orderId), { status: 'cancelled' });

  const celleIds = celle.map(c => c.id).filter(Boolean);
  const hasArea = Object.keys(areaQuantita).length > 0;

  if (!celleIds.length && !hasArea) return;

  await Promise.all(allDates.map(async (dateStr) => {
    const snap = await getDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr));
    if (!snap.exists()) return;
    const data = snap.data();
    const updates = {};

    if (celleIds.length) {
      const layout = data.layout || [];
      updates.layout = layout.map(c =>
        celleIds.includes(c.id) ? { ...c, status: 'free' } : c
      );
    }

    if (hasArea) {
      const current = data.areaPrenotazioni ?? {};
      const updatedArea = { ...current };
      for (const [srvId, qty] of Object.entries(areaQuantita)) {
        updatedArea[srvId] = Math.max(0, (updatedArea[srvId] ?? 0) - qty);
      }
      updates.areaPrenotazioni = updatedArea;
    }

    await updateDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr), updates);
  }));
}

export async function savePromozioni(beachCode, promozioni) {
  await updateDoc(doc(db, 'lidi', beachCode), { promozioni });
}

export async function getAreaPrenotazioni(beachCode, dateStr) {
  const snap = await getDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr));
  return snap.exists() ? (snap.data().areaPrenotazioni ?? {}) : {};
}

// ─── EVENTI ──────────────────────────────────────────────────────────────────

export async function getEventi(beachCode) {
  const snap = await getDocs(
    query(collection(db, `lidi/${beachCode}/eventi`), orderBy('data', 'asc'))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function saveEvento(beachCode, evento) {
  if (evento.id) {
    const { id, ...data } = evento;
    await setDoc(doc(db, `lidi/${beachCode}/eventi`, id), data, { merge: true });
    return id;
  } else {
    const ref = await addDoc(collection(db, `lidi/${beachCode}/eventi`), evento);
    return ref.id;
  }
}

export async function deleteEvento(beachCode, eventoId) {
  await deleteDoc(doc(db, `lidi/${beachCode}/eventi`, eventoId));
}

// ── Menu / ordini dall'ombrellone ─────────────────────────────────────────────

export async function getMenu(beachCode) {
  // Niente orderBy: l'ordine di display lo decide la UI raggruppando per
  // categoria e poi per `ordine` (manuale) o per `nome`. Lasciamo a Firestore
  // solo la fetch raw — costa meno indici.
  const snap = await getDocs(collection(db, `lidi/${beachCode}/menu`));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function saveMenuVoce(beachCode, voce) {
  if (voce.id) {
    const { id, ...data } = voce;
    await setDoc(doc(db, `lidi/${beachCode}/menu`, id), data, { merge: true });
    return id;
  } else {
    const ref = await addDoc(collection(db, `lidi/${beachCode}/menu`), voce);
    return ref.id;
  }
}

export async function deleteMenuVoce(beachCode, voceId) {
  await deleteDoc(doc(db, `lidi/${beachCode}/menu`, voceId));
}

// ── Ordini ombrellone (live) ─────────────────────────────────────────────────

/**
 * Sottoscrive lo snapshot live degli ordini del lido per la giornata corrente.
 * Filtra client-side per `createdAt` >= midnight di oggi — più semplice di una
 * query Firestore con range, e gli ordini di un singolo giorno sono pochi
 * (decine, raramente centinaia) quindi il costo è irrilevante.
 *
 * Ritorna `unsubscribe` da chiamare al cleanup del componente.
 */
export function watchOrdiniLidoOggi(beachCode, onChange) {
  const q = query(
    collection(db, `lidi/${beachCode}/ordiniLido`),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => {
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);
      const cutoffIso = todayMidnight.toISOString();
      const ordini = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(o => (o.createdAt ?? '') >= cutoffIso);
      onChange(ordini);
    },
    err => {
      console.error('[watchOrdiniLidoOggi] errore snapshot:', err);
      onChange([]);
    },
  );
}

export async function updateOrdineLidoStato(beachCode, ordineId, nuovoStato) {
  const patch = {
    stato: nuovoStato,
    updatedAt: new Date().toISOString(),
  };
  if (nuovoStato === 'pagato') patch['pagamento.pagatoAt'] = new Date().toISOString();
  await updateDoc(doc(db, `lidi/${beachCode}/ordiniLido`, ordineId), patch);
}